import { useContext, useMemo } from 'react';
import BaseViewsContext from '../../../../contexts/BaseViewsContext';
import useColumnDefs from '../../../../hooks/useColumnDefs';
import {
  SELECT_DOCUMENT_CELL,
  SELECT_DOCUMENT_HEADER_CELL,
} from '../../../Grid/SelectDocumentCell';
import type { ColDef } from 'ag-grid-community';
import { BonusRecordTaggingContext } from './BonusRecordTaggingProvider';
import { BONUS_TAG_PILL_CELL } from './BonusTagPillCell';

const useFilteredColumnDefs = (
  gridConfig: BonusRecordTagging.RecordTaggingConfig,
  showAll: boolean,
) => {
  const { baseViews } = useContext(BaseViewsContext);
  const { taggedDocumentIds } = useContext(BonusRecordTaggingContext);
  const baseView = baseViews[gridConfig.dataType];

  if (!baseView) {
    const e = new Error();
    e.name = `Base view not found for dataset (${gridConfig.dataType})`;
    throw e;
  }

  const sortConfig = useMemo(() => {
    if (gridConfig.sortBy) {
      return [
        { field: gridConfig.sortBy.field, direction: gridConfig.sortBy.sort },
      ];
    }
  }, [gridConfig.sortBy]);

  const columnDefs = useColumnDefs(baseView, true, sortConfig);

  const filteredColumnDefs: ColDef<any, any>[] = useMemo(() => {
    const filteredCols = columnDefs.filter((colDef) => {
      const mappedFields = showAll
        ? gridConfig.fields.map((f) => f.field)
        : gridConfig.fields.filter((f) => f.isDefault).map((f) => f.field);

      return colDef.field && mappedFields.includes(colDef.field);
    });

    const extraCols: ColDef<any, any>[] = [
      {
        field: '_selected',
        headerComponent: SELECT_DOCUMENT_HEADER_CELL,
        cellRenderer: SELECT_DOCUMENT_CELL,
        headerName: '',
        width: 38,
        menuTabs: [],
      },
    ];

    // Only show the tag pill column once at least one record carries the
    // configured tag — mirrors the original badge-column behaviour and avoids an
    // empty column when nothing is tagged. The cell reads the tagged set from
    // context so it updates reactively on save/remove.
    if (taggedDocumentIds.length) {
      extraCols.push({
        field: '_tagPill',
        cellRenderer: BONUS_TAG_PILL_CELL,
        headerName: '',
        menuTabs: [],
        width: 90,
      });
    }

    return [...extraCols, ...filteredCols];
  }, [columnDefs, gridConfig.fields, showAll, taggedDocumentIds]);

  return filteredColumnDefs;
};

export default useFilteredColumnDefs;
